// AuthProvider.js


import React, { useState } from 'react';
import { notification, Space, Spin } from 'antd';
// import { userFields } from "../compliance/Esireport";

import * as userService from '../services/auth';
import { setUpAxiosInterCeptors } from '../services/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { verifyToken } from '../utils/jwt';
import { setLoggedInUserAction } from '../../store/actions/authAction';
import { getAppStore, setAppStore } from '../../store';
import { decryptData } from '../utils/crypto';
import {
  findRolePermissions,
  canUseVinnyChat,
  canUseVynInsights,
  hasLicenceFeature,
  VINNY_CHAT_LICENCE,
  VYN_INSIGHTS_LICENCE,
} from '../utils/accessControl';

const lnt_orgs = ['LTHE', 'BAXTER']

const INITIAL_DATA = {
  user: {
    isAuthenticated: false,
  },
  isUserLoading: false,
  login: (data) => {},
  logout: (data) => {},
  userConfig: null,
  tokenInfo: null,
  modulePermissions: {},
  isModulePermissionsLoading: true,
};

export const AuthContext = React.createContext(INITIAL_DATA);

export default function AuthProvider({ children }) {
  const [isUserLoading, setUserLoading] = React.useState(false);
  const [isInfoLoading, setInfoLoading] = React.useState(true);
  const [user, setUser] = React.useState({
    isAuthenticated: false,
    userConfig: {},
    tokenInfo: [],
    token: {},
  });
  // const [fieldNames, setFieldNames] = useState(userFields)
  const [customDropdownValues, setCustomDropdownValues] = useState(null);
  const [permissionList, setPermissionList] = useState([]);
  // Capability object (permissions.cap) of the role assigned to the logged-in user.
  const [modulePermissions, setModulePermissions] = useState({});
  const [isModulePermissionsLoading, setModulePermissionsLoading] = useState(true);

  const navigate = useNavigate();

  React.useEffect(() => {
    setUpAxiosInterCeptors(navigate, setUser);
  }, [navigate]);

  React.useEffect(() => {
    getUserInfo();
    // Commenting temporarily because of impact in SSO login flow. #refactor
    // if (loggedInUser?.token) {
    //   getUserInfo();
    // } else {
    //   setInfoLoading(false);
    // }
  }, []);

  const getUserInfo = () => {
    userService
      .info()
      .then(async (res) => {
        // let decryptedUser;
        // try {
        //   decryptedUser = decryptData(res.data.data, res.data.iv);
        // } catch (error) {
        //   console.error('Failed to decrypt user data:', error);
        //   notification.error({ message: 'Failed to decrypt user data' });
        //   return;
        // } 
        // const decoded = await verifyToken(
        //   decryptedUser.organisation?.productLicenseKey || ''
        // );

        const decoded = await verifyToken(
          res?.data?.organisation?.productLicenseKey || res?.data?.productLicenseKey || ''
        );

        setUser({
          isAuthenticated: true,
          // userConfig: decryptedUser,
          userConfig: res?.data,
          tokenInfo: decoded?.features,
          token: res?.config?.headers?.token,
        });
      })
      .catch((err) => {
        console.error('Failed to get user info:', err);
      })
      .finally(() => {
        setInfoLoading(false);
      });
  };

  const { loggedInUser = {} } = getAppStore();
  React.useEffect(() => {
    if (loggedInUser.token) {
      getUserInfo();
    }
  }, [loggedInUser.token]);

  const orgId = user?.userConfig?.orgId;
  const userRole = user?.userConfig?.role;
  const userRoleType =
    typeof user?.userConfig?.roleName === 'object' && user?.userConfig?.roleName !== null
      ? user?.userConfig?.roleName?.selectedRoleType
      : user?.userConfig?.roleName;

  const userConfigRef = React.useRef(user?.userConfig);
  userConfigRef.current = user?.userConfig;

  // Load the capabilities of the role the user is assigned to. Features gated on
  // these permissions stay hidden until the fetch resolves, so nothing flashes
  // in for a user who is not allowed to see it.
  React.useEffect(() => {
    if (!user.isAuthenticated || !orgId) {
      setModulePermissions({});
      setModulePermissionsLoading(false);
      return undefined;
    }

    let isStale = false;
    setModulePermissionsLoading(true);

    const currentUserConfig = userConfigRef.current;
    const embeddedCap =
      currentUserConfig?.roleName?.permissions?.cap ||
      currentUserConfig?.roleName?.permissions ||
      currentUserConfig?.permissions?.cap ||
      currentUserConfig?.permissions;

    userService
      .getDetails(orgId)
      .then((res) => {
        if (isStale) return;
        const rolePermission = findRolePermissions(res?.data, userConfigRef.current);
        const fetchedCap = rolePermission?.permissions?.cap || rolePermission?.permissions;
        console.log('[VYN Auth] Role permission resolved:', {
          orgId,
          role: userConfigRef.current?.role,
          roleName: userConfigRef.current?.roleName,
          matchedRolePermission: rolePermission,
          fetchedCap,
        });
        setModulePermissions(fetchedCap || embeddedCap || {});
      })
      .catch((err) => {
        if (isStale) return;
        console.error('Failed to get role permissions:', err);
        setModulePermissions(embeddedCap || {});
      })
      .finally(() => {
        if (!isStale) setModulePermissionsLoading(false);
      });

    return () => {
      isStale = true;
    };
  }, [user.isAuthenticated, orgId, userRole, userRoleType]);

  const login = async (data) => {
    //
    setUserLoading(true);
    await userService
      .login(data)
      .then(async (res) => {
        setAppStore({ isLoader: false, loggedInUser: res.data })
        if (lnt_orgs.includes(res.data.orgId)) {
          window.location.reload();
        }
        sessionStorage.setItem('token', res.data.token);
        await setLoggedInUserAction(res.data);
        const decoded = await verifyToken(
          res?.data?.organisation?.productLicenseKey || res?.data?.productLicenseKey || ''
        );
        await setUser({
          isAuthenticated: true,
          userConfig: res.data,
          tokenInfo: decoded?.features,
          token: res.data.token,
        });
        if (res.data.type == 'self-onboarding') navigate(`/home/workman/${res.data._id}`)
      })
      .catch((err) => {
        if (err.response) {
          notification.error({ message: err.response.data.message });
        } 
        // else {
        //   notification.error({
        //     message: 'Please check your internet connection',
        //   });
        // }
      })
      .finally(() => setUserLoading(false));
  };

  const logout = () => {
    userService
      .logout()
      .then((res) => {
        const orgId = user?.userConfig?.orgId;
        // console.log(res, "val;ue of response");
        sessionStorage.removeItem('token');
        sessionStorage.clear();
        
        // Clear all SmartTable column filters from localStorage
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('smartFilters_')) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        
        setUser({
          isAuthenticated: false,
          userConfig: {},
        });
        setLoggedInUserAction({});

        // Okta signout for Freeman
        if(orgId?.toLowerCase() == 'freeman') {
          window.location.href = 'https://launch.freeman.com/login/signout';
        } else {
          window.location.reload();
        }

      })
      .catch((err) => {
        if (err.response) {
          notification.error({ message: err.response.data.message });
        } else {
          // notification.error({
          //   message: 'Please check your internet connection',
          // });
        }
      });
  };
  // const enable = (condition, fieldName) => {
  //   setFieldNames(
  //     fieldNames.map((data) => {
  //       if (data.fieldName === fieldName) {
  //         return {
  //           ...data,
  //           selected: condition,
  //         };
  //       } else {
  //         return data;
  //       }
  //     })
  //   );
  // };

  // Feature list carried by the organisation's licence key, decoded in
  // getUserInfo(). Undefined until that call resolves.
  const licensedFeatures = user.tokenInfo;

  const value = {
    user,
    userConfig: user.userConfig,
    // userConfig: user.userConfig.list,
    isUserLoading,
    login,
    logout,
    tokenInfo: user.tokenInfo,
    // fieldNames,
    // enable,
    customDropdownValues,
    setCustomDropdownValues,
    modulePermissions,
    isModulePermissionsLoading,
    // Features the organisation is licensed for (decoded productLicenseKey).
    licensedFeatures,
    // A feature is available only when the Super User licensed it for the
    // organisation AND the Super Admin granted it to the user's role name.
    canUseVinnyChat:
      !isModulePermissionsLoading &&
      canUseVinnyChat(modulePermissions, licensedFeatures),
    canUseVynInsights:
      !isModulePermissionsLoading &&
      canUseVynInsights(modulePermissions, licensedFeatures),
  };

  return (
    <AuthContext.Provider value={value}>
      {isInfoLoading ? (
        <Space
          style={{ width: '100vw', height: '100vh', justifyContent: 'center' }}>
          <Spin size='large'></Spin>
        </Space>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}
